import { Link, useParams } from "react-router-dom";
import { useAccounts } from "@/api/accounts";
import { Amount } from "@/components/Amount";
import { EmptyState, ErrorNote, Loading, PageTitle, buttonClass, rowsClass } from "@/components/ui";
import { useT } from "@/i18n/LocaleProvider";

/**
 * The account list, each with its derived balance.
 *
 * The balance is shown as its three terms rather than one number, because
 * "€412,30" answers a different question from "€500,00 to start, less €87,70
 * since" — and the second is the one that settles an argument with a bank
 * statement (`model.md` §4.1).
 *
 * Nothing here sums the accounts. They are each in their own currency and a sum
 * across currencies is meaningless; the household total is a base-currency
 * figure and belongs to the dashboard (K-26).
 */
export function AccountsPage() {
  const t = useT();
  const { householdId = "" } = useParams();
  const { data, isLoading, error } = useAccounts(householdId);

  return (
    <div>
      <PageTitle
        action={
          <Link to={`/households/${householdId}/accounts/new`} className={buttonClass}>
            {t("accounts.add")}
          </Link>
        }
      >
        {t("nav.accounts")}
      </PageTitle>

      {isLoading && <Loading />}
      <ErrorNote error={error} />

      {data && data.data.length === 0 && (
        <EmptyState title={t("accounts.emptyTitle")}>{t("accounts.emptyBody")}</EmptyState>
      )}

      {data && data.data.length > 0 && (
        <ul className={`mt-6 ${rowsClass}`}>
          {data.data.map((account) => (
            <li
              key={account.id}
              data-testid="account"
              className="flex items-baseline justify-between gap-4 py-4"
            >
              <div>
                <p className="font-medium text-ink">{account.name}</p>
                <p className="text-xs text-muted">
                  {account.currency_code}
                  {account.institution_name ? ` · ${account.institution_name}` : ""}
                  {account.iban_last4 ? ` · ····${account.iban_last4}` : ""}
                </p>
              </div>

              <div className="text-right">
                <Amount
                  minor={account.balance.total_minor}
                  currency={account.balance.currency_code}
                  className="block font-medium"
                />
                <p className="text-xs text-muted">
                  <Amount
                    minor={account.balance.opening_minor}
                    currency={account.balance.currency_code}
                    symbol={false}
                  />{" "}
                  {t("accounts.opening")} ·{" "}
                  <Amount
                    minor={account.balance.movement_minor}
                    currency={account.balance.currency_code}
                    symbol={false}
                  />{" "}
                  {t("accounts.movement")}
                </p>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
